import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import INFINITE_SCROLL_CONFIG from '../../../config/infiniteScrollConfig';
import { infiniteScrollUtils } from '../../../config/infiniteScrollConfig';

export const useVentas = () => {
  const { token } = useAuth();
  const [ventas, setVentas] = useState([]);
  // Buscar venta por ID
  const searchById = useCallback(async (ventaId) => {
    if (!ventaId) return;
    setLoading(true);
    setIsSearching(true);
    setError(null);
    try {
      const url = `${import.meta.env.VITE_API_URL}/ventas/admin/${ventaId}`;
      const response = await axios.get(url, {
        headers: { 'x-auth-token': token },
        timeout: INFINITE_SCROLL_CONFIG.REQUEST_TIMEOUT,
      });
      if (response.data && response.data.venta) {
        setVentas([response.data.venta]);
        setTotalVentas(1);
        setHasMoreVentas(false);
      } else {
        setVentas([]);
        setTotalVentas(0);
        setHasMoreVentas(false);
        setError('No se encontró la venta con ese ID.');
      }
    } catch (err) {
      setVentas([]);
      setTotalVentas(0);
      setHasMoreVentas(false);
      setError('No se encontró la venta con ese ID.');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [token]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreVentas, setHasMoreVentas] = useState(true);
  const [totalVentas, setTotalVentas] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  
  // (eliminado: declaración duplicada de token)
  const abortControllerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Log de estado para debugging (simplificado)
  console.log('🔍 State:', {
    hasMore: hasMoreVentas,
    loading,
    loadingMore,
    isSearching,
    page: currentPage,
    count: ventas.length
  });

  const fetchVentas = useCallback(async (fechaInicio = '', fechaFin = '', page = 1, resetVentas = true) => {
    if (!token) {
      console.log('❌ No token available, skipping fetch');
      return;
    }

    try {
      // Cancelar request anterior si existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Crear nuevo AbortController
      abortControllerRef.current = new AbortController();

      if (page === 1) {
        setLoading(true);
        // Solo marcar como "searching" si hay filtros de fecha
        if (fechaInicio && fechaFin) {
          setIsSearching(true);
        }
      } else {
        setLoadingMore(true);
      }

      // Construir URL usando la configuración centralizada
      const url = infiniteScrollUtils.buildApiUrl(
        import.meta.env.VITE_API_URL, 
        page, 
        fechaInicio, 
        fechaFin
      );

      const response = await axios.get(url, {
        headers: {
          'x-auth-token': token,
        },
        signal: abortControllerRef.current.signal,
        timeout: INFINITE_SCROLL_CONFIG.REQUEST_TIMEOUT,
      });

      // Si el componente fue desmontado, no actualizar estado
      if (abortControllerRef.current.signal.aborted) {
        return;
      }

      const { 
        ventas: nuevasVentas, 
        currentPage: responsePage, 
        totalCount, 
        hasMore
      } = response.data;

      console.log('📦 Response data:', {
        ventasLength: nuevasVentas?.length || 0,
        currentPage: responsePage || page,
        totalCount,
        hasMore,
        isFirstPage: page === 1 || resetVentas
      });

      if (resetVentas || page === 1) {
        setVentas(nuevasVentas || []);
      } else {
        setVentas(prevVentas => {
          const existingIds = new Set(prevVentas.map(v => String(v.id)));
          const toAppend = (nuevasVentas || []).filter(v => !existingIds.has(String(v.id)));
          const newVentas = [...prevVentas, ...toAppend];
          console.log('🔢 Total ventas after append (deduped):', newVentas.length, 'appended:', toAppend.length);
          return newVentas;
        });
      }

      setCurrentPage(responsePage || page);
      setHasMoreVentas(hasMore || false);
      setTotalVentas(totalCount || 0);
      setError(null);

      console.log('📊 State updated:', {
        currentPage: responsePage || page,
        hasMoreVentas: hasMore || false,
        totalVentas: totalCount || 0
      });

      // Log de performance usando utilidad centralizada
      infiniteScrollUtils.logPerformance(response.data, page);

    } catch (err) {
      if (err.name !== 'CanceledError' && !abortControllerRef.current?.signal.aborted) {
        const errorType = infiniteScrollUtils.getErrorType(err);
        
        switch (errorType) {
          case INFINITE_SCROLL_CONFIG.ERROR_TYPES.RATE_LIMIT:
            setError('Demasiadas consultas. Por favor, espera un momento antes de intentar nuevamente.');
            break;
          case INFINITE_SCROLL_CONFIG.ERROR_TYPES.VALIDATION:
            setError(err.response?.data?.message || 'Parámetros de búsqueda inválidos.');
            break;
          case INFINITE_SCROLL_CONFIG.ERROR_TYPES.SERVER:
            setError('Error del servidor. Por favor, intenta nuevamente más tarde.');
            break;
          default:
            setError('Hubo un error al obtener las ventas. Intenta nuevamente.');
        }
        console.error('Error fetching ventas:', err);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setIsSearching(false);
    }
  }, [token]);

  // Búsqueda con debounce mejorado
  const debouncedSearch = useCallback((fechaInicio, fechaFin) => {
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Establecer nuevo timeout
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      setHasMoreVentas(true);
      fetchVentas(fechaInicio, fechaFin, 1, true);
    }, INFINITE_SCROLL_CONFIG.DEBOUNCE_DELAY);
  }, [fetchVentas]);

  // Cargar más ventas para infinite scroll
  const loadMoreVentas = useCallback((fechaInicio = '', fechaFin = '') => {
    console.log('🚀 loadMore:', {
      hasMore: hasMoreVentas,
      loading,
      loadingMore,
      isSearching,
      page: currentPage
    });

    if (hasMoreVentas && !loadingMore && !loading && !isSearching) {
      console.log('✅ Loading page:', currentPage + 1);
      fetchVentas(fechaInicio, fechaFin, currentPage + 1, false);
    } else {
      console.log('❌ Blocked:', {
        hasMore: hasMoreVentas,
        loadingMore,
        loading,
        isSearching
      });
    }
  }, [hasMoreVentas, loadingMore, loading, isSearching, currentPage, fetchVentas]);

  // Buscar ventas por fecha con validación
  const searchByDates = useCallback((fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) {
      setError('Por favor, selecciona ambas fechas.');
      return;
    }

    if (new Date(fechaInicio) > new Date(fechaFin)) {
      setError('La fecha de inicio no puede ser mayor que la fecha de fin.');
      return;
    }

    const validation = infiniteScrollUtils.validateDateRange(fechaInicio, fechaFin);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    debouncedSearch(fechaInicio, fechaFin);
  }, [debouncedSearch]);

  // Reset de filtros
  const resetFilters = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setCurrentPage(1);
    setHasMoreVentas(true);
    setError(null);
    setIsSearching(false); // Resetear el estado de búsqueda
    fetchVentas('', '', 1, true);
  }, [fetchVentas]);

  // Limpiar error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Inicializar datos cuando el token esté disponible
  useEffect(() => {
    if (token) {
      console.log('🚀 Initializing data...');
      setCurrentPage(1);
      setHasMoreVentas(true);
      setError(null);
      setIsSearching(false);
      fetchVentas('', '', 1, true);
    }
  }, [token, fetchVentas]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Auto-dismiss error
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, INFINITE_SCROLL_CONFIG.ERROR_AUTO_DISMISS);

      return () => clearTimeout(timer);
    }
  }, [error]);

  return {
    // Estado
    ventas,
    loading,
    loadingMore,
    error,
    currentPage,
    hasMoreVentas,
    totalVentas,
    isSearching,
    
    // Acciones
    fetchVentas,
    loadMoreVentas,
    searchByDates,
    resetFilters,
    clearError,
  debouncedSearch,
  searchById
  };
};
